import { Suspense, lazy, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { m, AnimatePresence } from "framer-motion";
import { Header } from "./Header";
import { BottomNav } from "./BottomNav";
import { ChannelSwitcher } from "./ChannelSwitcher";
import { AdminHub } from "./AdminHub";
import { StaleVersionBanner } from "./StaleVersionBanner";
import { CardGridSkeleton } from "./Skeletons";
import { useBackButton } from "../hooks/useBackButton";
import { useEnrollFromStartParam } from "../hooks/channels";
import { WebApp } from "../lib/telegram";

// Every route is a lazy import — the initial bundle excludes unvisited pages
// (frontend.md performance target).
const SearchScreen = lazy(() => import("../screens/SearchScreen"));
const BrowseScreen = lazy(() => import("../screens/BrowseScreen"));
const CollectionsScreen = lazy(() => import("../screens/CollectionsScreen"));
const CollectionDetailScreen = lazy(() => import("../screens/CollectionDetailScreen"));
const ItemScreen = lazy(() => import("../screens/ItemScreen"));
const ProfileScreen = lazy(() => import("../screens/ProfileScreen"));

export function Shell() {
  const location = useLocation();
  const qc = useQueryClient();
  useBackButton();
  useEnrollFromStartParam();

  // App lifecycle — on return from minimised state, refresh stale data and let
  // the "new since last visit" feed recompute (frontend.md).
  useEffect(() => {
    const onActivated = () => qc.invalidateQueries();
    const on = WebApp.onEvent as unknown as (e: string, cb: () => void) => void;
    const off = WebApp.offEvent as unknown as (e: string, cb: () => void) => void;
    on?.("activated", onActivated);
    return () => off?.("activated", onActivated);
  }, [qc]);

  return (
    <div
      className="flex flex-col overflow-hidden bg-tg-bg text-tg-text"
      style={{ height: "var(--viewport-height)" }}
    >
      <StaleVersionBanner />
      <Header />

      <main className="relative min-h-0 flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          <m.div
            key={routeKey(location.pathname)}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
            className="h-full"
          >
            <Suspense fallback={<div className="px-4 pt-3"><CardGridSkeleton /></div>}>
              <Routes location={location}>
                <Route path="/" element={<SearchScreen />} />
                <Route path="/browse" element={<BrowseScreen />} />
                <Route path="/collections" element={<CollectionsScreen />} />
                <Route path="/collections/:cid" element={<CollectionDetailScreen />} />
                <Route path="/item/:messageId" element={<ItemScreen />} />
                <Route path="/profile" element={<ProfileScreen />} />
              </Routes>
            </Suspense>
          </m.div>
        </AnimatePresence>
      </main>

      <BottomNav />

      {/* Global overlays */}
      <ChannelSwitcher />
      <AdminHub />
    </div>
  );
}

// Animate only between distinct screens, not within a screen's param changes
// (e.g. typing in search must not re-trigger the page transition).
function routeKey(pathname: string): string {
  if (pathname.startsWith("/item/")) return "item";
  if (pathname.startsWith("/collections/")) return "collection-detail";
  return pathname;
}
